// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md § 5.2
// AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
import notAttributedText from '../msiMonetisationHelpers/notAttributedText'

/**
 * @typedef {object} KoppelingRij
 * @property {string} key - Stabiele sleutel voor de render
 * @property {string} label - Wat er gemeten is (de participatieladder of een effect)
 * @property {string} [effectId] - OutputEffects.ID, alleen bij een effectregel
 * @property {number|null} count - Aantal gekoppelde deelnemers, of null als dat aantal niet bekend is
 * @property {string|null} reason - Waarom deze regel niet meetelt; null bij een meetellende regel
 * @property {string|null} reasonCode - Vaste code bij de reden (zie koppelingAdvies); null bij een meetellende regel
 */

/**
 * @typedef {object} KoppelingOverzicht
 * @property {number} total - Het aantal dat tegen de drempel van twintig wordt gelegd
 * @property {Array<KoppelingRij>} counted - Regels die meetellen
 * @property {Array<KoppelingRij>} notCounted - Regels die niet meetellen, elk met zijn eigen reden
 */

// Redenen bij een versieprobleem van een effect (scoresForEffect). Tot
// 2026-11-04 heetten die 'Geen bedragen in de Meetstandaard', terwijl de
// bedragen er wel zijn: de gebruiker wachtte dan op een nieuwe versie die
// niets zou oplossen.
const VERSIE_REDEN = {
    'gemengde-versies': ['versies-gemengd', 'Vragen uit verschillende versies van de Meetstandaard'],
    'geen-versie': ['versie-ontbreekt', 'Versie van de Meetstandaard onbekend'],
    'onbekende-versie': ['versie-onbekend', 'Deze versie van de Meetstandaard kent Deccos nog niet'],
}

/**
 * Waarom een effect niet meetelt, als vaste code en als tekst. De volgorde is
 * de beslisvolgorde: een uitgesloten effect wordt niet opgehaald, een effect
 * zonder bedragen evenmin, dus die twee gaan vóór alles wat over de antwoorden
 * zelf gaat.
 *
 * @param {{excluded?: boolean, excludedReason?: string, countedByName?: string, noScores?: boolean, versionProblem?: string|null, momentCount?: number, perPersona?: Array<object>}} entry
 * @returns {[string, string]|null} [code, tekst], of null als het effect wél meetelt
 */
const effectReasonWithCode = (entry) => {
    if (entry?.excluded && entry?.excludedReason === 'samengevoegd') {
        return ['telt-al-mee-samengevoegd', `Telt al mee via ${entry.countedByName || 'een ander effect'}`]
    }
    if (entry?.excluded) return ['telt-al-mee-ladder', 'Telt al mee via de participatieladder']
    if (entry?.noScores) return VERSIE_REDEN[entry?.versionProblem] || ['geen-bedragen', 'Geen bedragen in de Meetstandaard']

    const momenten = Number(entry?.momentCount) || 0
    if (momenten === 0) return ['nog-niet-gemeten', 'Nog niet gemeten']
    if (momenten === 1) return ['een-meetmoment', 'Nog maar één meetmoment']

    const gekoppeld = Array.isArray(entry?.perPersona) ? entry.perPersona.length : 0
    if (gekoppeld === 0) return ['geen-deelnemer-twee-momenten', 'Geen deelnemer op twee momenten gemeten']

    return null
}

/**
 * Waarom een effect niet meetelt, als tekst.
 * @param {object} entry - Zie effectReasonWithCode
 * @returns {string|null} de reden, of null als het effect wél meetelt
 */
const effectReason = (entry) => {
    const reden = effectReasonWithCode(entry)
    return reden ? reden[1] : null
}

/**
 * De code bij 'niet doorgerekend', uit de telling per reden van de rekenregels.
 * @param {Object<string, number>} [reasons]
 * @returns {'duur-uren-onbekend'|'einde-voor-start'|'duur-uren-of-einde'}
 */
const notAttributedCode = (reasons = {}) => {
    const keys = Object.keys(reasons || {}).filter((key) => reasons[key] > 0)
    const einde = keys.includes('einde vóór start')
    const rest = keys.some((key) => key !== 'einde vóór start')
    if (einde && rest) return 'duur-uren-of-einde'
    return einde ? 'einde-voor-start' : 'duur-uren-onbekend'
}

/**
 * Splitst de teller achter de drempel van twintig (§ 5.2) uit naar wat wél en
 * wat níét meetelt, mét de reden per regel.
 *
 * WAAROM DIT BESTAAT. De drempel kan op vijf manieren nee zeggen en boekte ze
 * allemaal op één getal. Dan doet de poort het goed, maar liegt de diagnose:
 * een organisatie die haar onderzoek compleet heeft ingevuld zag "4 van de 20"
 * en kon onmogelijk weten dat er zeven effecten afvielen om een reden die niet
 * bij haar ligt (de Meetstandaard had er nog geen bedragen voor).
 *
 * GEEN VERZONNEN AANTALLEN. Een effect zonder bedragen wordt niet opgehaald —
 * de antwoorden zijn er dus niet — en krijgt `count: null` in plaats van 0. Een
 * 0 zou lezen als "niemand gemeten", wat precies het tegenovergestelde is van
 * wat er aan de hand is.
 *
 * Sinds de rekenregels van de MSI telt een meting zonder duur of uren niet
 * mee: zij voedt de teller niet. Die metingen staan in 'Telt niet mee' met de
 * reden, per stroom en per effect.
 *
 * Elke regel draagt een `reasonCode`, zodat het scherm erbij kan zeggen wat
 * de gebruiker eraan kan doen (koppelingAdvies).
 *
 * @param {object} input
 * @param {{linkedMeasurementCount: number, zonderMetingInPeriode: number, zonderHoofdactiviteit: number, notAttributedCount?: number, notAttributedReasons?: Object<string, number>}|null} input.participation - Uitkomst van sumParticipationPeriodValue; null zolang de stroom laadt
 * @param {Array<object>} input.msiPerEffect - `perEffect` uit useMsiMonetaryTotal
 * @returns {KoppelingOverzicht|null} null zolang de participatiestroom niet geladen is
 */
const buildLinkedMeasurementBreakdown = ({ participation, msiPerEffect } = {}) => {
    if (!participation) return null

    /** @type {Array<KoppelingRij>} */
    const counted = []
    /** @type {Array<KoppelingRij>} */
    const notCounted = []

    const gekoppeld = Number(participation.linkedMeasurementCount) || 0
    if (gekoppeld > 0) {
        counted.push({
            key: 'participatieladder',
            label: 'Participatieladder',
            count: gekoppeld,
            reason: null,
            reasonCode: null,
        })
    }

    const zonderMeting = Number(participation.zonderMetingInPeriode) || 0
    if (zonderMeting > 0) {
        notCounted.push({
            key: 'participatieladder-geen-meting',
            label: 'Participatieladder',
            count: zonderMeting,
            reason: 'Geen meting in dit boekjaar',
            reasonCode: 'geen-meting-in-boekjaar',
        })
    }

    const zonderActiviteit = Number(participation.zonderHoofdactiviteit) || 0
    if (zonderActiviteit > 0) {
        notCounted.push({
            key: 'participatieladder-geen-hoofdactiviteit',
            label: 'Participatieladder',
            count: zonderActiviteit,
            reason: 'Geen hoofdactiviteit ingevuld',
            reasonCode: 'geen-hoofdactiviteit',
        })
    }

    const nietDoorgerekend = Number(participation.notAttributedCount) || 0
    if (nietDoorgerekend > 0) {
        notCounted.push({
            key: 'participatieladder-niet-doorgerekend',
            label: 'Participatieladder',
            count: nietDoorgerekend,
            reason: notAttributedText(null, participation.notAttributedReasons),
            reasonCode: notAttributedCode(participation.notAttributedReasons),
        })
    }

    ;(Array.isArray(msiPerEffect) ? msiPerEffect : []).forEach((entry) => {
        const effectId = entry?.effectId || ''
        const rij = {
            key: `effect-${effectId}`,
            label: entry?.name || 'Naamloos effect',
            effectId,
        }
        const reden = effectReasonWithCode(entry)

        if (reden === null) {
            // Rekenregels: alleen wie doorgerekend kon worden voedt de teller
            const zonder = entry.perPersona.filter((p) => p.attributed === null)
            const met = entry.perPersona.length - zonder.length
            if (met > 0) counted.push({ ...rij, count: met, reason: null, reasonCode: null })
            if (zonder.length > 0) {
                const reasons = {}
                zonder.forEach((p) => { reasons[p.doseReason] = (reasons[p.doseReason] || 0) + 1 })
                notCounted.push({
                    ...rij,
                    key: `${rij.key}-niet-doorgerekend`,
                    count: zonder.length,
                    reason: notAttributedText(null, reasons),
                    reasonCode: notAttributedCode(reasons),
                })
            }
            return
        }

        notCounted.push({ ...rij, count: null, reason: reden[1], reasonCode: reden[0] })
    })

    return {
        total: counted.reduce((sum, row) => sum + row.count, 0),
        counted,
        notCounted,
    }
}

export default buildLinkedMeasurementBreakdown
export { effectReason }
